import { Box, useTheme } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimeField } from '@mui/x-date-pickers/TimeField'
import dateToTimeString from 'components/calendar/components'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { minutesToHumanReadable } from 'components/popups/eventInfo/AddReminderPopup'
import SelectItemWithSearchPopup, {
  SelectableItem
} from 'components/popups/SelectItemWithSearchPopup'
import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import i18n from 'i18n'
import { MutableRefObject } from 'react'
import { Controller } from 'react-hook-form'
import Logger from 'services/Logger'
import { isCypress } from 'utils/cypress'
import { isAMPM } from 'utils/date'

export interface TimePickerBoxProps {
  allDay: boolean

  dayjsTime: Dayjs
  timeFormat?: (timestamp: number) => string
  shouldShowTimeDropdown?: boolean
  showTimeMinutesPopup: boolean
  setShowTimeMinutesPopup: (show: boolean) => void
  buildMinutes: () => SelectableItem[]
  onAcceptDate: (date: Dayjs | null, time?: boolean) => void
  setTime: (date: Dayjs | null) => void
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
  tz: string
  isStartPicker: boolean
  withUnderline?: boolean
  shouldScrollToTime?: boolean
  readonly?: boolean
  actionId?: string
  expanded?: boolean
}

export interface TimeRowProps {
  item: SelectableItem
  setShowTimeMinutesPopup: (show: boolean) => void
  onAcceptDate: (date: Dayjs | null, time?: boolean) => void
}

dayjs.extend(utc)
dayjs.extend(timezone)

export function buildTimeRow(props: TimeRowProps) {
  const { item, setShowTimeMinutesPopup, onAcceptDate } = { ...props }
  return (
    <Box
      key={item.title}
      id={item.title}
      onClick={(e) => {
        e.stopPropagation()
        setShowTimeMinutesPopup(false)
        onAcceptDate(item.value, true)
      }}
      width={'fit-content'}
      display={'flex'}
      alignContent={'center'}
      flexDirection={'row'}
      color={'var(--color-text-primary)'}
      sx={{
        cursor: 'pointer',
        fontSize: '14px',

        '&:hover': {
          backgroundColor: 'var(--color-surface-hover)'
        }
      }}
      gap={4}
    >
      <div
        style={{
          padding: '4px',
          minWidth: isAMPM() ? '70px' : '45px'
        }}
      >
        {item.title}
      </div>
      {item.subTitle && (
        <div
          style={{
            color: 'var(--color-text-muted)',
            textAlign: 'center',
            padding: '4px'
          }}
        >
          {item.subTitle}
        </div>
      )}
    </Box>
  )
}

export function buildTimeOptions(
  baseTime: Dayjs,
  duration?: number,
  _subtract: boolean = false,
  wholeDay: boolean = false,
  subTitle: boolean = false,
  referenceTime?: Dayjs,
  timezone?: string
): SelectableItem[] {
  Logger.blue('time-picker', 'Building time options', baseTime, referenceTime)
  const minutes: SelectableItem[] = []

  if (referenceTime !== undefined) {
    for (let i = 1; i < 5; i++) {
      const minuteOffset = i * 15
      let time: Dayjs

      time = referenceTime.add(minuteOffset, 'minute')

      const selectableTime: SelectableItem = {
        title: dateToTimeString(time.toDate(), true, undefined, timezone),
        subTitle: !subTitle ? undefined : minutesToHumanReadable(minuteOffset),
        value: time
      }
      minutes.push(selectableTime)
    }
  }

  for (let i = wholeDay ? 0 : 1; i < (wholeDay ? 48 : 7); i++) {
    const minuteOffset = (i + 1) * 30
    let time: Dayjs

    time =
      duration !== undefined
        ? baseTime.add(0 - duration, 'milliseconds').add(minuteOffset, 'minute')
        : baseTime.add(minuteOffset, 'minute')

    const selectableTime: SelectableItem = {
      title: dateToTimeString(time.toDate(), true, undefined, timezone),
      subTitle: !subTitle
        ? undefined
        : minutesToHumanReadable(
            time.diff(referenceTime ?? baseTime, 'minute') * -1
          ),
      value: time
    }
    minutes.push(selectableTime)
  }
  return minutes
}

export function TimePickerBox({
  allDay,

  dayjsTime,
  timeFormat,
  shouldShowTimeDropdown,
  showTimeMinutesPopup,
  setShowTimeMinutesPopup,
  buildMinutes,
  onAcceptDate,
  setTime,
  timeoutRef,
  tz,
  isStartPicker,
  withUnderline,
  shouldScrollToTime,
  readonly,
  actionId,
  expanded
}: TimePickerBoxProps) {
  const theme = useTheme()
  const timePickerInputId = expanded
    ? isStartPicker
      ? 'start-time-picker-expanded'
      : 'end-time-picker-expanded'
    : isStartPicker
      ? 'start-time-picker'
      : 'end-time-picker'
  const sharedUnderlineColor =
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.34)'
      : 'rgba(0, 0, 0, 0.32)'
  if (allDay) return <></>

  function togglePopupVisibility(b: boolean) {
    setShowTimeMinutesPopup(b)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <SelectItemWithSearchPopup
        data={buildMinutes()}
        width="fit-content"
        maxHeight="110px"
        placement="bottom"
        scrollToKey={
          shouldScrollToTime
            ? dateToTimeString(
                dayjsTime.startOf('hour').toDate(),
                true,
                undefined,
                tz
              )
            : undefined
        }
        snapScroll
        open={showTimeMinutesPopup}
        selectedItemId={undefined}
        rowBuilder={(item) => {
          return buildTimeRow({
            item,
            setShowTimeMinutesPopup: togglePopupVisibility,
            onAcceptDate
          })
        }}
      >
        <PopupSectionRow
          actionBg
          actionId={actionId}
          gap="0rem"
          flexGrowForRow={0}
          actionPadding="4px 0px"
          action={
            readonly
              ? undefined
              : () => {
                  togglePopupVisibility(true)
                }
          }
          title={
            <TimeField
              key={dayjsTime.valueOf()}
              ampm={isCypress() ? false : isAMPM()}
              defaultValue={dayjsTime}
              variant="standard"
              inputProps={{
                id: timePickerInputId,
                style: {
                  cursor: readonly ? 'default' : 'pointer',
                  fontSize: '14px',
                  color: theme.palette.text.primary,
                  padding: 0,

                  textAlign: 'center',
                  height: '100%',
                  maxWidth: isAMPM() ? '70px' : '45px',

                  borderBottom: withUnderline
                    ? `1px solid ${sharedUnderlineColor}`
                    : 'none'
                }
              }}
              onBlur={() => {}}
              InputProps={{ disableUnderline: true }}
              onChange={(newValue) => {
                togglePopupVisibility(false)

                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current)
                }

                timeoutRef.current = setTimeout(() => {
                  // TimeField emits temporary invalid values while the user types.
                  // Ignore these transitional states to avoid propagating invalid dates.
                  if (!newValue || !newValue.isValid()) {
                    return
                  }

                  setTime(newValue)
                  onAcceptDate(newValue, true)
                }, 500)
              }}
            />
          }
        />
      </SelectItemWithSearchPopup>
    </LocalizationProvider>
  )
}
